import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { toast } from 'sonner';

const empty = { title: '', slug: '', content: '', excerpt: '', template: 'default', published: true, show_in_header: false, show_in_footer: true, order: 0, seo_title: '', seo_description: '', seo_keyphrase: '' };

const AdminPages = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState('content');

  const load = () => api.listPages().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setTab('content'); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...empty, ...p }); setTab('content'); setOpen(true); };
  const save = async () => {
    if (!form.title || !form.slug) return toast.error('Title and slug are required');
    try {
      if (editing) await api.updatePage(editing, form);
      else await api.createPage(form);
      toast.success(editing ? 'Updated' : 'Created');
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try { await api.deletePage(id); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400">Build your storefront pages — About, Contact, Shipping, FAQ, Privacy, Terms or anything custom. Markdown supported.</p>
        <Button onClick={openNew} className="btn-gold"><Plus className="h-4 w-4 mr-2" /> New page</Button>
      </div>

      <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60"><tr className="text-left text-zinc-400"><th className="p-3">Title</th><th className="p-3">Slug</th><th className="p-3">Template</th><th className="p-3">In header</th><th className="p-3">In footer</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">No pages yet</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                <td className="p-3 text-zinc-100">{p.title}</td>
                <td className="p-3 text-zinc-500 font-mono text-xs">/{p.slug}</td>
                <td className="p-3 text-zinc-300">{p.template}</td>
                <td className="p-3">{p.show_in_header ? <Badge variant="outline" className="border-emerald-600 text-emerald-400">Yes</Badge> : <Badge variant="outline" className="border-zinc-700 text-zinc-500">No</Badge>}</td>
                <td className="p-3">{p.show_in_footer ? <Badge variant="outline" className="border-emerald-600 text-emerald-400">Yes</Badge> : <Badge variant="outline" className="border-zinc-700 text-zinc-500">No</Badge>}</td>
                <td className="p-3"><Badge variant="outline" className={p.published ? 'border-emerald-600 text-emerald-400' : 'border-amber-600 text-amber-400'}>{p.published ? 'Published' : 'Draft'}</Badge></td>
                <td className="p-3 text-right">
                  <Link to={`/p/${p.slug}`} target="_blank"><Button size="sm" variant="ghost" className="text-zinc-300 hover:text-[#d4af37]"><ExternalLink className="h-4 w-4" /></Button></Link>
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-[#d4af37]" onClick={() => openEdit(p)}><Edit className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-rose-500" onClick={() => del(p.id)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-900 text-zinc-100 max-w-3xl max-h-[92vh] overflow-auto">
          <DialogHeader><DialogTitle className="serif-italic text-[#d4af37] text-2xl">{editing ? 'Edit page' : 'New page'}</DialogTitle></DialogHeader>
          <div className="flex gap-2 mt-2 border-b border-zinc-800">
            {['content', 'visibility', 'seo'].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm capitalize transition-colors ${tab === t ? 'text-[#d4af37] border-b-2 border-[#d4af37] -mb-px' : 'text-zinc-400 hover:text-zinc-200'}`}>{t}</button>
            ))}
          </div>
          {tab === 'content' && (
            <div className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') })} className="bg-transparent border-zinc-800 mt-1" /></div>
                <div><Label>Slug (URL: /p/&lt;slug&gt;)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              </div>
              <div><Label>Excerpt</Label><Input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Short summary shown in lists" className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Template</Label>
                <Select value={form.template} onValueChange={(v) => setForm({ ...form, template: v })}>
                  <SelectTrigger className="bg-transparent border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default — Centered text</SelectItem>
                    <SelectItem value="fullwidth">Full-width — Edge-to-edge</SelectItem>
                    <SelectItem value="contact">Contact — Form + map</SelectItem>
                    <SelectItem value="faq">FAQ — Accordion-style</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Content (markdown supported)</Label><Textarea rows={14} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-transparent border-zinc-800 mt-1 font-mono text-sm" /><p className="text-zinc-500 text-xs mt-1">Use ##, ###, **bold**, *italic*, - bullets. Plain paragraphs separated by blank lines.</p></div>
            </div>
          )}
          {tab === 'visibility' && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between border border-zinc-900 rounded p-3"><div><p className="text-zinc-200">Published</p><p className="text-zinc-500 text-xs">Visible on the storefront</p></div><Switch checked={!!form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /></div>
              <div className="flex items-center justify-between border border-zinc-900 rounded p-3"><div><p className="text-zinc-200">Show in header navigation</p><p className="text-zinc-500 text-xs">Appears in the top menu next to Shop</p></div><Switch checked={!!form.show_in_header} onCheckedChange={(v) => setForm({ ...form, show_in_header: v })} /></div>
              <div className="flex items-center justify-between border border-zinc-900 rounded p-3"><div><p className="text-zinc-200">Show in footer</p><p className="text-zinc-500 text-xs">Appears in the footer link list</p></div><Switch checked={!!form.show_in_footer} onCheckedChange={(v) => setForm({ ...form, show_in_footer: v })} /></div>
              <div><Label>Display order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
            </div>
          )}
          {tab === 'seo' && (
            <div className="space-y-4 mt-4">
              <div><Label>SEO Title</Label><Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /><p className="text-zinc-500 text-xs mt-1">50–60 chars. Include page focus keyphrase. Defaults to page title if empty.</p></div>
              <div><Label>SEO Description</Label><Textarea rows={3} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /><p className="text-zinc-500 text-xs mt-1">140–160 chars. Compelling description with focus keyphrase.</p></div>
              <div><Label>Focus keyphrase</Label><Input value={form.seo_keyphrase} onChange={(e) => setForm({ ...form, seo_keyphrase: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
            </div>
          )}
          <div className="mt-6 flex justify-end gap-3"><Button variant="outline" className="btn-outline-gold" onClick={() => setOpen(false)}>Cancel</Button><Button className="btn-gold" onClick={save}>{editing ? 'Update' : 'Create'}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPages;
